// fallow-ignore-file duplicate-export
import dayjs from 'dayjs';
/* oxlint-disable-next-line import/no-unassigned-import */
import 'dayjs/locale/de';
/* oxlint-disable-next-line import/no-unassigned-import */
import 'dayjs/locale/en';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Register plugins
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);

dayjs.locale('de'); // Globally select German locale by default -> TODO: link this to language switch

export type DateLike = Date | string | number;

export type DateRange = [string, string];

export { default as dayjs } from 'dayjs';

type TimeFormat = 'HH:MM' | 'HH:MM:SS';

// A date string from a spreadsheet is YYYY-MM-DD, optionally carrying a time
// part separated by "T" or a space and an optional zone designator. Time and
// zone components are range-checked (23:59:59 at most): a value with an
// impossible time such as "29:99" must fall through unchanged so schema
// validation reports it, instead of being silently truncated to its date.
const SPREADSHEET_DATE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d)?)?$/;

function isCalendarDate(year: number, month: number, day: number): boolean {
  // Setting the components on the epoch avoids Date.UTC mapping years below
  // 100 into the 1900s, which would reject them as out of range.
  const probe = new Date(0);
  probe.setUTCFullYear(year, month - 1, day);

  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * Normalizes date-like values from spreadsheet imports to an ISO date string
 * (YYYY-MM-DD). `readXlsxFileAsync` returns date cells as Excel serial
 * numbers, CSV parsing returns strings, and either source may carry
 * timestamps — this maps all three to the format Zod's `z.iso.date()`
 * accepts.
 *
 * A date string keeps the calendar date it spells out. Reading it back off a
 * parsed `Date` in UTC would move a local time shortly after midnight to the
 * previous day for anyone east of Greenwich.
 *
 * If the value cannot be interpreted as a date, the original value is
 * returned as a string so schema validation reports it instead of crashing.
 *
 * @param value - An Excel serial number, a timestamp, or a date string.
 * @returns An ISO date string (YYYY-MM-DD), or the stringified input when it is not a parseable date.
 */
export function normalizeDateToISOString(value: unknown): string {
  if (typeof value === 'string' && value) {
    const [, year, month, day] = SPREADSHEET_DATE_PATTERN.exec(value) ?? [];

    if (!year || !month || !day) {
      return value;
    }

    if (!isCalendarDate(Number(year), Number(month), Number(day))) {
      return value;
    }

    return `${year}-${month}-${day}`;
  }

  if (typeof value === 'number') {
    let date: Date;
    // Distinguish between Excel serial numbers and timestamps
    // Excel serial numbers are typically < 100,000 (covers dates well into the future)
    // Timestamps are much larger (milliseconds since 1970)
    if (value < 100_000) {
      // Excel treats 1900 as a leap year, so its day count contains the
      // phantom 1900-02-29 (serial 60). The 1899-12-30 epoch below already
      // absorbs that extra day for serials above 60; serials below 60
      // (Jan 1 - Feb 28, 1900) predate the phantom day and need one added.
      const offset = value < 60 ? 1 : 0;
      // We use Date.UTC to prevent timezone offsets during conversion.
      // The Excel epoch starts on 1899-12-30.
      date = new Date(Date.UTC(1899, 11, 30 + value + offset));
    } else {
      // Assume it's a timestamp
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toISOString().split('T')[0]!;
  }

  return String(value);
}

/**
 * Normalizes a time string to HH:MM or HH:MM:SS format.
 * Handles:
 * - Truncating or preserving seconds (e.g. "08:00:00" → "08:00" or "08:00:00")
 * - Hour-only input (e.g. "8" or "08" → "08:00" or "08:00:00")
 * - Hour and minute input (e.g. "8:30" → "08:30" or "08:30:00")
 * Returns null, undefined, and empty strings unchanged.
 * Returns undefined for non-parseable strings.
 */
export function normalizeTimeValue<T extends string | null | undefined>(
  value: T,
  format: TimeFormat = 'HH:MM',
): T {
  if (typeof value !== 'string' || !value) {
    return value;
  }

  const [hours, minutes, seconds] = value
    .split(':')
    .map((v) => parseInt(v, 10));

  if (Number.isNaN(hours)) {
    return undefined as T;
  }

  const parts =
    format === 'HH:MM:SS' ? [hours, minutes, seconds] : [hours, minutes];

  return parts.map((v) => String(v || 0).padStart(2, '0')).join(':') as T;
}
